import moment from "moment";
import {
  CONTACT_POINT_SYSTEM,
  MAX_FEES_VALUE,
  REGEX_EXPS,
  SALE_MODES,
} from "utils/constants";
import { isMajor } from "utils/helpers/date";
import {
  softValidateNIR,
  validateBIC,
  validateIBAN,
  validateNIR,
} from "utils/helpers/values";
import * as yup from "yup";

const validation = (messages, { hasPartner, checkUser }) => {
  const string = yup.string().typeError(messages.NOT_STRING).nullable();
  const nameValidation = yup
    .string()
    .typeError(messages.NOT_STRING)
    .nullable()
    .notRequired()
    .test(
      "is-valid-name",
      messages.INVALID_NAME,
      (value) => !value || REGEX_EXPS.name.test(value),
    );

  const relatedPersonValidationSchema = ({ birthDate }) =>
    yup.object().shape({
      identity: yup.object().shape({
        user: yup.object().shape({
          lastname: nameValidation,
          firstname: nameValidation,
          birthDate,
        }),
        socioProfessionalCategory: string,
        socialRegime: string,
        socialSecurityNumber: yup
          .string()
          .typeError(messages.REQUIRED)
          .nullable()
          .optional()
          .test(
            "securityNumberFormat",
            messages.INVALID_VALUE,
            function (value, ctx) {
              if (!value) return true;
              const result = validateNIR(value, this.parent);
              if (!result.valid)
                return ctx.createError({
                  message: messages[result.reason] || messages.INVALID_VALUE,
                });

              return true;
            },
          ),
      }),
    });
  return yup.object().shape({
    createdDate: yup.date(),
    isPayerReceiver: yup.string().typeError(messages.NOT_STRING),
    additionalInfo: yup
      .object()
      .shape({
        brokerageFees: yup
          .number()
          .max(MAX_FEES_VALUE, `${messages.MAX} ${MAX_FEES_VALUE}`)
          .min(0, `${messages.MIN} 0`)
          .nullable()
          .optional(),
      })
      .optional(),
    project: yup.object().shape({
      projectType: yup.mixed().when("contract.saleMode", {
        is: (saleMode) => saleMode === SALE_MODES.DISTANT,
        then: string,
      }),
      contract: yup.object().shape({
        issuanceDate: yup.date(),
        caseType: string,
        saleMode: string,
      }),
      fields: yup.object().shape({
        selfOccupation: yup.boolean(),
        contractNumber: string,
        dueDate: yup.date(),
        recipient: string,
        reason: string,
      }),
      terminationRequest: yup
        .object()
        .nullable()
        .shape({
          contractNumber: string,
          dueDate: yup.date().typeError(messages.REQUIRED).nullable(),
          recipient: string,
          reason: string,
        }),
      contact: yup.object().shape({
        user: yup.object().shape({
          gender: string,
          lastname: nameValidation,
          firstname: nameValidation,
          birthName: nameValidation,
          birthDate: yup.date(),
        }),
        socialRegime: string,
        socialSecurityNumber: yup
          .string()
          .typeError(messages.REQUIRED)
          .nullable()
          .optional()
          .test(
            "securityNumberFormat",
            messages.INVALID_VALUE,
            function (value, ctx) {
              if (!value) return true;
              const result = validateNIR(value, this.parent);
              if (!result.valid)
                return ctx.createError({
                  message: messages[result.reason] || messages.INVALID_VALUE,
                });

              return true;
            },
          ),
        socioProfessionalCategory: string,
        politicallyExposedPerson: yup.boolean().nullable(),
        // exerciseFrame: yup.mixed().when("socioProfessionalCategory", {
        //   is: showExerciseFrame,
        //   then: string,
        // }),
        siretNumber: string,
        profession: string,
        acceptContact: yup.boolean().typeError(messages.NOT_BOOLEAN).nullable(),
        acceptDigital: yup.boolean().typeError(messages.NOT_BOOLEAN).nullable(),
        familySituation: string,
        fields: yup.object().shape({
          affiliateOrganizationCode: yup
            .string()
            .typeError(messages.NOT_STRING)
            .nullable(),
        }),
      }),
    }),
    telecoms: yup.object().shape({
      contact: yup.object().shape({
        [CONTACT_POINT_SYSTEM.ADDRESS]: yup.object().shape({
          value: string,
        }),
        [CONTACT_POINT_SYSTEM.COMPLEMENT]: yup.object().shape({
          value: string,
        }),
        [CONTACT_POINT_SYSTEM.ZIP_CODE]: yup.object().shape({
          value: string,
        }),
        [CONTACT_POINT_SYSTEM.CITY]: yup.object().shape({
          value: string,
        }),
        [CONTACT_POINT_SYSTEM.DEPARTMENT_OF_BIRTH]: yup.object().shape({
          value: string,
        }),
        [CONTACT_POINT_SYSTEM.BIRTH_COUNTRY]: yup.object().shape({
          value: string,
        }),
        [CONTACT_POINT_SYSTEM.PHONE]: yup.object().shape({
          value: yup
            .string()
            .typeError(messages.REQUIRED)
            .nullable()
            .test("isValid", messages.INVALID_VALUE, function (value) {
              return value
                ? new RegExp(REGEX_EXPS.internationalMobilePhone).test(
                    value.replace(/\s+/g, ""),
                  )
                : true;
            }),
          // .test(
          //   "phoneConflict",
          //   messages.PHONE_CONFLICT,
          //   async function (value) {
          //     const isValidRegex = new RegExp(REGEX_EXPS.phone).test(value);
          //     if (!value || !isValidRegex) {
          //       return true;
          //     }
          // const {
          //   data: { user },
          // } = await checkUser({
          //   variables: {
          //     where: { phone: value },
          //   },
          // });
          // return !user?.id;
          // }
          // ),
        }),
        [CONTACT_POINT_SYSTEM.LANDLINE]: yup.object().shape({
          value: yup
            .string()
            .typeError(messages.REQUIRED)
            .nullable()
            .test("isValid", messages.INVALID_VALUE, async function (value) {
              return value ? new RegExp(REGEX_EXPS.phone).test(value) : true;
            }),
        }),
        [CONTACT_POINT_SYSTEM.EMAIL]: yup.object().shape({
          value: yup.string().nullable().email(messages.EMAIL),
          // .test(
          //   "emailConflict",
          //   messages.EMAIL_CONFLICT,
          //   async function (value) {
          //     if (!value || !yup.string().email().isValidSync(value)) {
          //       return true;
          //     }
          //     const {
          //       data: { user },
          //     } = await checkUser({
          //       variables: {
          //         where: { email: value },
          //       },
          //     });
          //     return !user?.id;
          //   }
          // ),
        }),
      }),
      payer: yup.object().shape({
        [CONTACT_POINT_SYSTEM.ADDRESS]: yup.object().shape({
          value: string,
        }),
        [CONTACT_POINT_SYSTEM.ZIP_CODE]: yup.object().shape({
          value: string,
        }),
        [CONTACT_POINT_SYSTEM.CITY]: yup.object().shape({
          value: string,
        }),
      }),
      receiver: yup.object().shape({
        // [CONTACT_POINT_SYSTEM.ADDRESS]: yup.object().shape({
        //   value: string.required(messages.REQUIRED),
        // }),
        // [CONTACT_POINT_SYSTEM.COMPLEMENT]: yup.object().shape({
        //   value: string,
        // }),
        // [CONTACT_POINT_SYSTEM.ZIP_CODE]: yup.object().shape({
        //   value: string.required(messages.REQUIRED),
        // }),
        // [CONTACT_POINT_SYSTEM.CITY]: yup.object().shape({
        //   value: string.required(messages.REQUIRED),
        // }),
      }),
    }),
    spouse:
      hasPartner &&
      relatedPersonValidationSchema({
        birthDate: yup
          .date()
          .typeError(messages.REQUIRED)
          .max(isMajor, messages.RADIANCE.SPOUSE_AGE)
          .min(
            moment().subtract(100, "year").startOf("year"),
            messages.RADIANCE.SPOUSE_AGE,
          )
          .required(messages.REQUIRED),
      }),
    children: yup.array().of(
      yup.object().shape({
        identity: yup.object().shape({
          user: yup.object().shape({
            lastname: nameValidation,
            firstname: nameValidation,
            birthDate: yup
              .date()
              .typeError(messages.REQUIRED)
              .max(moment(), messages.MIN_TODAY)
              .required(messages.REQUIRED),
          }),
          socioProfessionalCategory: string,
          socialRegime: string,
          socialSecurityNumber: yup
            .string()
            .typeError(messages.REQUIRED)
            .nullable()
            .optional()
            .test(
              "securityNumberFormat",
              messages.INVALID_VALUE,
              function (value, ctx) {
                if (!value) return true;
                const result = softValidateNIR(value);
                if (!result.valid)
                  return ctx.createError({
                    message: messages[result.reason] || messages.INVALID_VALUE,
                  });

                return true;
              },
            ),
        }),
      }),
    ),
    payment: yup.object().shape({
      type: string,
      frequency: string,
      payer: yup.object().shape({
        BIC: yup
          .string()
          .typeError(messages.NOT_STRING)
          .nullable()
          .test(
            "validateBIC",
            messages.INVALID_BIC || messages.INVALID_IBAN,
            function (value) {
              if (!value) return true;
              const result = validateBIC(value);
              return result.valid;
            },
          ),
        IBAN: yup
          .string()
          .typeError(messages.NOT_STRING)
          .nullable()
          .test("validateIBAN", messages.INVALID_IBAN, function (value) {
            if (!value) return true;
            const result = validateIBAN(value);
            return result.valid;
          }),
        owner: yup.object().shape({
          isAdherent: yup.boolean().typeError(messages.NOT_BOOLEAN),
          firstName: nameValidation,
          lastName: nameValidation,
        }),
      }),
      receiver: yup.object().shape({
        BIC: yup
          .string()
          .typeError(messages.NOT_STRING)
          .nullable()
          .test(
            "validateBIC",
            messages.INVALID_BIC || messages.INVALID_IBAN,
            function (value) {
              if (!value) return true;
              const result = validateBIC(value);
              return result.valid;
            },
          ),
        IBAN: yup
          .string()
          .typeError(messages.NOT_STRING)
          .nullable()
          .test("validateIBAN", messages.INVALID_IBAN, function (value) {
            if (!value) return true;
            const result = validateIBAN(value);
            return result.valid;
          }),
        owner: yup.object().shape({
          firstName: nameValidation,
          lastName: nameValidation,
        }),
      }),
    }),
  });
};

export default validation;
